import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import type { Row } from "./api";
import { ChangeProgramme } from "./ChangeProgramme";
import { CreateProgramme } from "./CreateProgramme";
import { useAdmin } from "./context";
import {
  changed, chg, comfort, day, dpd, hasRef, isTest, KIND, kindOf, L, ml, planStatus, refLine, svChanged, tookAns, versions, weekA, when, yn,
} from "./format";
import { NoteDialog } from "./NoteDialog";
import { ProgressChart } from "./ProgressChart";
import { Empty, IcLink, Loading, LoadError, PageHead, RefChip, StatusChip, TestChip, useServerNow, WaitingChip } from "./parts";

export function CustomerDetail({ id }: { id: string }) {
  const { key, api, me, openCustomer, openQueue } = useAdmin();
  const client = useQueryClient();
  const [notice, setNotice] = useState<string | null>(null);
  const [changing, setChanging] = useState(false);
  const enc = encodeURIComponent(id);
  const detail = useQuery({ queryKey: [key, "customer", id], queryFn: () => api.get<Row>(`customers/${enc}`) });
  // A failed versions call falls back to the history built from check-ins and staff changes, as the Worker does.
  const sv = useQuery({
    queryKey: [key, "customer", id, "versions"],
    queryFn: () => api.get<Row[]>(`customers/${enc}/versions`).catch(() => null),
  });
  const now = useServerNow(detail.data?.server_now, detail.dataUpdatedAt);
  const refresh = () => client.invalidateQueries({ queryKey: [key, "customer", id] });

  const back = (
    <Button variant="link" className="px-0 text-ks-grey-1" data-nonmutating onClick={() => openCustomer(null)}>
      ← Customers
    </Button>
  );
  if (detail.isLoading) return <div>{back}<Loading /></div>;
  if (detail.error || !detail.data) return <div>{back}<LoadError what="load" error={detail.error ?? "not found"} /></div>;

  const d = detail.data;
  const p: Row = d.plan || {};
  const pr: Row = d.profile || {};
  const alerts: Row[] = d.alerts || [];
  const handoffs: Row[] = d.handoffs || [];
  const openA = alerts.filter((a) => !a.handled_at);
  const openH = handoffs.filter((h) => !h.handled_at);
  const writes = !!me?.writes;
  const paused = p.status === "paused";
  const status = planStatus(d.plan);
  const test = isTest(pr);

  const markHandled = (kind: "alert" | "handoff", hid: string) =>
    writes ? (
      <NoteDialog
        label="Mark handled"
        title="Mark handled"
        description="Optional note: what was done?"
        failure="Couldn’t mark it handled. Nothing changed. Try again."
        onSave={async (note) => {
          const r = await api.post<Row>("handled", { kind, id: hid, note });
          setNotice(r.already_handled ? `Already handled by ${r.handled_by}.` : "Marked handled.");
          refresh();
          void client.invalidateQueries({ queryKey: [key, "queue"] });
        }}
      />
    ) : null;

  return (
    <div>
      {back}
      <PageHead
        title={
          <span className="flex flex-wrap items-center gap-2">
            {pr.name || pr.email} <StatusChip status={status} />
            {test ? <TestChip /> : <WaitingChip w={d.reply_due} now={now} onOpen={openQueue} />}
          </span>
        }
        sub={pr.name ? pr.email : undefined}
        right={
          status === "none" ? (
            <CreateProgramme first={String(pr.name || "").trim().split(/\s+/)[0] || "She"} />
          ) : writes && d.plan ? (
            <ChangeProgramme id={id} d={d} sv={sv.data ?? null} openA={openA} openH={openH} open={changing}
              onOpenChange={setChanging} onSaved={(m) => { setNotice(m); refresh(); }} />
          ) : (
            <Button disabled title="Switched off until the app follows a staff change (S4).">Change programme</Button>
          )
        }
      />
      {notice ? (
        <Alert className="mb-4" role="status"><AlertDescription>{notice}</AlertDescription></Alert>
      ) : null}
      {paused ? (
        <Alert className="mb-4">
          <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
            <span>Plan paused on {day(p.pause?.at)} after {pauseWhy(p.pause)}. Nothing moves until you set the next dose.</span>
            {writes ? <Button size="sm" variant="secondary" onClick={() => setChanging(true)}>Set next dose</Button> : null}
          </AlertDescription>
        </Alert>
      ) : null}
      {openA.length || openH.length ? (
        <Card className="mb-4">
          <CardContent className="space-y-2 text-sm">
            {openA.map((a) => (
              <div key={a.id} className="flex flex-wrap items-center gap-2">
                {hasRef(a.referral) ? (
                  <><RefChip /> {refLine(a.referral)}</>
                ) : (
                  <span>Team alert: <b>{L(a.type)}</b> ({L(a.reason)})</span>
                )}
                <span>, {when(a.at)}</span>
                {a.ticket ? <span>· <IcLink id={a.ticket} /></span> : null}
                {markHandled("alert", a.id)}
              </div>
            ))}
            {openH.map((h) => (
              <div key={h.id} className="flex flex-wrap items-center gap-2">
                <span>Analyst hand-off: {h.goal || ""}, {when(h.at)}</span>
                {h.ticket ? <span>· <IcLink id={h.ticket} /></span> : null}
                {markHandled("handoff", h.id)}
              </div>
            ))}
          </CardContent>
        </Card>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Card>
            <CardContent className="text-sm">
              {d.plan ? <Programme p={p} last={((d.checkins || []) as Row[]).find((c) => c.kind !== "start")} /> : <Empty>No plan yet.</Empty>}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Planned journey to target</CardTitle>
              <Journey sv={sv.data ?? null} />
            </CardHeader>
            <CardContent>
              <ProgressChart plan={p} checkins={d.checkins || []} now={now} />
              <p className="mt-2 text-sm text-ks-grey-1">
                {p.reaches_target_date
                  ? `Reaches ${ml(p.target_dose_ml)} on ${day(p.reaches_target_date)} if ${her(pr)} stomach stays fine. `
                  : ""}
                Every change replots this chart and keeps the old version in the history.
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle>Check-ins</CardTitle></CardHeader>
            <CardContent className="text-sm">
              <Average d={d} writes={writes} onReset={async (note) => {
                const r = await api.post<Row>(`customers/${enc}/reset-average`, { note });
                const n = r.referrals_closed;
                setNotice(`Average reset.${n ? ` ${n} referral${n === 1 ? "" : "s"} closed.` : ""}`);
                refresh();
              }} />
              <Checkins d={d} />
            </CardContent>
          </Card>
        </div>
        <div className="space-y-4">
          <Card>
            <CardHeader><CardTitle>Change history</CardTitle></CardHeader>
            <CardContent className="text-sm">
              <p className="mb-2 text-ks-grey-1">Newest first: only what changed. Click one for the full programme.</p>
              <div className="max-h-[420px] overflow-auto">
                {Array.isArray(sv.data) ? <ServerHistory sv={sv.data} d={d} /> : <LocalHistory d={d} />}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle>Intercom</CardTitle></CardHeader>
            <CardContent className="text-sm">
              <p className="mb-2 text-ks-grey-1">Tickets and conversations, newest first.</p>
              <Intercom id={id} d={d} />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

/** The design's words are "she / her": the app's customers are addressed that way on the signed-off frames (DEC-1164). */
const her = (_pr: Row) => "her";

/** "Plan paused on 27 Oct after a severely upset stomach." The check-in's reason key, or the staff member's words. */
const PAUSE_WHY: Record<string, string> = {
  rung_4_stop: "a severely upset stomach", rung_4_stop_again: "a severely upset stomach again",
  emulsify_declined_stop: "she declined the emulsified option", severely_upset: "a severely upset stomach", severe_reaction: "a severe reaction", stopped_again: "stopping again",
};
export function pauseWhy(pause: Row | null | undefined): string {
  if (!pause) return "a pause";
  const k = typeof pause.reason_key === "string" ? pause.reason_key : "";
  if (PAUSE_WHY[k]) return PAUSE_WHY[k];
  const r = typeof pause.reason === "string" ? pause.reason.trim() : "";
  if (PAUSE_WHY[r]) return PAUSE_WHY[r];
  if (pause.by === "staff") return `a staff change${pause.staff_email ? ` by ${pause.staff_email}` : ""}${r ? `: “${r}”` : ""}`;
  return r ? L(r).toLowerCase() : "a check-in";
}

/** "current journey v4, made 28 Oct by the algorithm": the newest programme version. */
function Journey({ sv }: { sv: Row[] | null }) {
  const x = Array.isArray(sv) ? sv[0] : null;
  if (!x) return null;
  return (
    <p className="text-xs text-ks-grey-1">
      current journey v{x.version_no}, made {day(x.at)} by {x.source === "staff" ? x.staff_email : "the algorithm"}
    </p>
  );
}

function Kv({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-ks-grey-4 p-2">
      <span className="block text-xs text-ks-grey-1">{label}</span>
      <b>{value}</b>
    </div>
  );
}

/** PA3 / PA3P tiles, in the design's order. */
export function tiles(p: Row, last: Row | undefined): [string, string][] {
  const paused = p.status === "paused";
  const n = p.step_count ?? (Array.isArray(p.plan_steps) ? p.plan_steps.length : null);
  const at = p.step_index;
  return [
    ["Current dose", paused ? `Paused (was ${ml(p.dose_ml)})` : ml(p.dose_ml)],
    ["Target dose", `${ml(p.target_dose_ml)}${n ? ` (step ${n})` : ""}`],
    ["Took at last check-in", !last ? "—" : last.took_choice === "didnt" ? tookAns(last) : last.took_ml == null ? "—" : ml(last.took_ml)],
    ["Step", at == null || !n ? "—" : `${at} of ${n} · ${paused ? "paused" : ml(p.dose_ml)}`],
    ["Stomach comfort", comfort(p.rung)],
    ["Weekly increase", weekA(p.weekly_step_ml)],
    ["Goal", p.goal || p.goal_level_2 || "—"],
    ["Product", p.product || "—"],
    ["Next check-in", paused
      ? "None while paused"
      : p.change_pending
        ? "7 days after she accepts"
        : p.next_checkin_date
          ? `${day(p.next_checkin_date)}${p.next_checkin_day != null ? ` · Day ${p.next_checkin_day}` : ""}`
          : "—"],
    ["Algorithm", `V1${p.release ? ` (model ${p.release})` : ""}`],
  ];
}

function Programme({ p, last }: { p: Row; last: Row | undefined }) {
  return (
    <>
      <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
        {tiles(p, last).map(([k, v]) => <Kv key={k} label={k} value={v} />)}
      </div>
      <div className="mt-3 rounded-lg bg-ks-purple-4 p-3">
        <span className="block text-xs text-ks-grey-1">How to take it (she sees this)</span>
        {p.instructions || <span className="text-ks-grey-1">No instructions yet.</span>}
      </div>
      {p.change_pending ? (
        <Alert className="mt-3">
          <AlertDescription>
            Waiting for her to accept the latest change in the app. Her next check-in is due 7 days after she accepts.
          </AlertDescription>
        </Alert>
      ) : p.override_id && p.accepted_at ? (
        <p className="mt-1 text-ks-grey-1">Latest change accepted {when(p.accepted_at)}</p>
      ) : null}
      {p.pinned_release ? (
        <p className="mt-1 text-ks-grey-1">Protocol <b>pinned to {p.pinned_release}</b> (default {p.default_release})</p>
      ) : null}
    </>
  );
}

/** 1141 / 1143: the results average the referral uses (since the plan's start or the last reset). */
function Average({ d, writes, onReset }: { d: Row; writes: boolean; onReset: (note: string) => Promise<void> }) {
  const a: Row | null = d.checkin_average;
  const earlier = ((d.alerts || []) as Row[]).filter((x) => x.handled_at && hasRef(x.referral));
  return (
    <>
      {a ? (
        <p className="mb-3 flex flex-wrap items-center gap-2">
          <span>
            Results average <b>{a.average ?? "—"}</b> from {a.scores ?? 0} rating{a.scores === 1 ? "" : "s"}{" "}
            <span className="text-ks-grey-1">
              since {a.average_from ? `${day(a.average_from)} (reset by ${a.reset_by || "staff"})` : "the plan started"}
            </span>
          </span>
          {writes ? (
            <NoteDialog
              label="Reset average"
              title="Reset the results average to now?"
              description="Earlier ratings stop counting and the referral check runs again later. Optional note:"
              onSave={onReset}
            />
          ) : null}
        </p>
      ) : null}
      {/* a handled referral leaves the queue and the open-alert box; its numbers stay here (newest first) */}
      {earlier.map((x) => (
        <p key={x.id} className="mb-2 text-ks-grey-1">
          Earlier referral, {day(x.at)}: {refLine(x.referral)} · handled {day(x.handled_at)}
          {x.handled_by ? ` by ${x.handled_by}` : ""}
        </p>
      ))}
    </>
  );
}

/** "79.8 (−0.3 · −3.2)": the reading, then since last · total (canvas thread #1). */
export function withChange(v: unknown, since: number | null | undefined, total: number | null | undefined): string {
  if (v == null || v === "") return "—";
  const f = (x: number | null | undefined) => (x == null ? "—" : chg(x, "").trim());
  return `${v} (${f(since)} · ${f(total)})`;
}
/** A check-in with nothing taken and nothing answered: the hollow dot on the chart, "Missed" in the table. */
export const missed = (c: Row) => c.missed === true;

/**
 * Check-ins and missed weeks as one list, newest first. A missed week has no row in the database: the API sends
 * the empty 7-day slots as plan.missed_checkins, so they read "Missed" here and a hollow dot on the chart.
 */
export function checkinRows(d: Row): Row[] {
  const gaps = (((d.plan || {}).missed_checkins || []) as Row[]).map((m) => ({
    id: `missed-${m.date}`, at: `${m.date}T12:00:00Z`, day: m.day, step: m.step, previous_dose_ml: m.dose_ml, missed: true,
  }));
  const t = (x: Row) => new Date(x.at).getTime() || 0;
  return [...((d.checkins || []) as Row[]), ...gaps].sort((a, b) => t(b) - t(a));
}

function Checkins({ d }: { d: Row }) {
  const cs: Row[] = checkinRows(d);
  if (!cs.length) return <Empty>No check-ins yet.</Empty>;
  const tk: Record<string, string> = {};
  for (const a of (d.alerts || []) as Row[]) if (a.checkin_id && a.ticket) tk[a.checkin_id] = a.ticket;
  const heads: [string, string?][] = [
    ["Date"], ["Day"], ["Target"], ["Took"], ["Stomach"], ["Results rating"], ["Weight kg (since last · total)"],
    ["Waist cm (since last · total)"], ["Sleep worse", "Collected only: the algorithm does not use it"],
    ["Less hungry", "Collected only: the algorithm does not use it"], ["Ticket", "Intercom ticket opened by this check-in"],
  ];
  return (
    <>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>{heads.map(([h, t], i) => <TableHead key={i} title={t}>{h}</TableHead>)}</TableRow>
          </TableHeader>
          <TableBody>
            {cs.map((c) => {
              const m = missed(c);
              return (
                <TableRow key={c.id}>
                  <TableCell className="whitespace-nowrap">{day(c.at)}</TableCell>
                  <TableCell className="whitespace-nowrap">{c.kind === "start" ? <b>Start</b> : c.day != null ? `Day ${c.day}` : "—"}</TableCell>
                  <TableCell>{ml(c.kind === "start" ? c.dose_ml : c.previous_dose_ml ?? c.dose_ml)}</TableCell>
                  <TableCell>{m ? "Missed" : c.took_choice === "didnt" ? tookAns(c) : ml(c.took_ml)}</TableCell>
                  <TableCell>{m ? "—" : L(c.stomach)}</TableCell>
                  <TableCell>{m || c.results_score == null || c.results_score === "" ? "—" : String(c.results_score)}</TableCell>
                  <TableCell className="whitespace-nowrap tabular-nums">{m ? "—" : withChange(c.weight_kg, c.weight_since_last, c.weight_total)}</TableCell>
                  <TableCell className="whitespace-nowrap tabular-nums">{m ? "—" : withChange(c.waist_cm, c.waist_since_last, c.waist_total)}</TableCell>
                  <TableCell>{yn(c.sleep_worse)}</TableCell>
                  <TableCell>{yn(c.less_hungry)}</TableCell>
                  <TableCell>
                    {tk[c.id] ? <IcLink id={tk[c.id]}><Badge variant="outline" className="text-ks-warn">● Ticket</Badge></IcLink> : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      {d.checkin_count > (d.checkins || []).length ? (
        <p className="mt-2 text-ks-grey-1">
          Showing the newest {(d.checkins || []).length} of {d.checkin_count}. Totals count from the oldest check-in shown, not the first one.
        </p>
      ) : null}
    </>
  );
}

/** Intercom list (PF 7 Oct, D10/D11): title + link only, no message content, no AI. Tickets the app opened are marked. */
const IC_NOTE: Record<string, string> = {
  intercom_off: "Intercom is not connected here.",
  no_contact: "No Intercom contact for this email.",
  intercom_error: "Intercom did not answer. Try again later.",
};
function Intercom({ id, d }: { id: string; d: Row }) {
  const { key, api } = useAdmin();
  const ic = useQuery({
    queryKey: [key, "customer", id, "intercom"],
    queryFn: () => api.get<Row>(`customers/${encodeURIComponent(id)}/intercom`).catch(() => ({ items: [], note: "intercom_error" })),
  });
  if (ic.isLoading) return <Empty>Loading…</Empty>;
  const r: Row = ic.data || { items: [] };
  const fromApp = new Set([...(d.alerts || []), ...(d.handoffs || [])].map((x: Row) => x.ticket).filter(Boolean).map(String));
  if (!r.items?.length) return <Empty>{IC_NOTE[r.note] || "No tickets or conversations."}</Empty>;
  return (
    <ul className="max-h-[420px] divide-y divide-ks-grey-4 overflow-auto">
      {(r.items as Row[]).map((x) => (
        <li key={x.id} className="py-2">
          <div className="flex flex-wrap items-center gap-1 text-xs text-ks-grey-1">
            {when(x.at)} ·{" "}
            {x.kind === "ticket" ? <Badge variant="outline">Ticket{x.type ? `: ${x.type}` : ""}</Badge> : <Badge variant="secondary">Conversation</Badge>}
            {fromApp.has(String(x.id)) ? <Badge variant="outline" className="text-ks-warn">From the app</Badge> : null}
            {x.state ? ` · ${x.state}` : ""}
          </div>
          <IcLink id={x.id}>{x.title || "(no title)"}</IcLink>
        </li>
      ))}
    </ul>
  );
}

function VersionItem({ head, body, quote, full }: { head: React.ReactNode; body: React.ReactNode; quote?: React.ReactNode; full: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="cursor-pointer py-2" onClick={(e) => { if (!(e.target as HTMLElement).closest("a")) setOpen(!open); }}>
      <div className="flex flex-wrap items-center gap-1 text-xs text-ks-grey-1">{head}</div>
      <div>{body}</div>
      {quote ? <div className="text-ks-grey-1">{quote}</div> : null}
      {open ? <div className="mt-2 rounded-lg bg-ks-purple-4 p-2">{full}</div> : null}
    </li>
  );
}

function Snapshot({ x, server }: { x: Row; server: boolean }) {
  const { product } = useAdmin();
  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        <Kv label="Dose each time" value={ml(x.dose_ml)} />
        <Kv label="Stomach comfort" value={comfort(x.rung)} />
        <Kv label="Product" value={product(x.product_key)} />
        <Kv label="Status" value={x.paused ? "Paused" : "On plan"} />
        {server ? <Kv label="Goal" value={x.goal_level_2 || "—"} /> : null}
        {server ? <Kv label="Doses a day" value={dpd(x.doses_per_day)} /> : null}
        {server && x.release ? <Kv label="Protocol version" value={x.release} /> : null}
      </div>
      {x.target_dose_ml != null ? <p className="mt-1 text-ks-grey-1">Target {ml(x.target_dose_ml)}</p> : null}
      {server && x.instructions ? <p className="mt-1"><span className="text-ks-grey-1">How to take it:</span> {x.instructions}</p> : null}
    </>
  );
}

/** Server versions (admin_program_versions, dosing 23): newest first, each with the fields that changed (D11). */
function ServerHistory({ sv, d }: { sv: Row[]; d: Row }) {
  const names = useAdmin();
  if (!sv.length) return <Empty>No changes since start.</Empty>;
  const ov: Record<string, Row> = {};
  for (const o of (d.overrides || []) as Row[]) ov[o.id] = o;
  const p: Row = d.plan || {};
  const tkt = (aid: string) => ((d.alerts || []) as Row[]).find((a) => a.id === aid)?.ticket;
  return (
    <ul className="divide-y divide-ks-grey-4">
      {sv.map((x, i) => {
        const o = ov[x.ref_id] || {};
        const b = sv[i + 1] || null;
        const staff = x.source === "staff";
        const acc = !staff || x.kind === "pause" ? null : o.accepted_at ? (
          <Badge variant="outline" className="text-ks-pass">Accepted {day(o.accepted_at)}</Badge>
        ) : p.change_pending && p.override_id === x.ref_id ? (
          <Badge variant="outline" className="text-ks-warn">Awaiting her accept</Badge>
        ) : null;
        return (
          <VersionItem
            key={`${x.version_no}-${x.ref_id}`}
            head={
              <>
                v{x.version_no} · {when(x.at)} ·{" "}
                {staff ? (
                  <><Badge variant="outline" className="text-ks-pass">{x.staff_email}</Badge> · {x.case_id} · {names.caseType(x.case_type)}{acc ? <> · {acc}</> : null}</>
                ) : (
                  <Badge variant="secondary">Algorithm</Badge>
                )}
              </>
            }
            body={
              <>
                {staff ? <b>{kindOf(x)}: </b> : null}
                {svChanged(x, b, names)}
                {!staff ? <span className="text-ks-grey-1"> ({L(x.reason)})</span> : null}
              </>
            }
            quote={staff && x.reason ? (
              <>“{x.reason}”{o.team_alert_id && tkt(o.team_alert_id) ? <> · <IcLink id={tkt(o.team_alert_id)} /></> : null}</>
            ) : undefined}
            full={<Snapshot x={x} server />}
          />
        );
      })}
    </ul>
  );
}

function LocalHistory({ d }: { d: Row }) {
  const names = useAdmin();
  const V = versions(d);
  if (!V.length) return <Empty>No changes since start.</Empty>;
  const tkt = (aid: string) => ((d.alerts || []) as Row[]).find((a) => a.id === aid)?.ticket;
  return (
    <ul className="divide-y divide-ks-grey-4">
      {[...V].reverse().map((x) => (
        <VersionItem
          key={x.n}
          head={
            <>
              v{x.n} · {when(x.at)} ·{" "}
              {x.src === "staff" ? (
                <><Badge variant="outline" className="text-ks-pass">{x.staff}</Badge> · {x.case_id} · {names.caseType(x.case_type)}</>
              ) : (
                <Badge variant="secondary">Algorithm {x.engine || ""}</Badge>
              )}
            </>
          }
          body={
            <>
              {x.src === "staff" ? <b>{KIND[x.kind] || x.kind}: </b> : null}
              {changed(x)}
              {x.src === "alg" ? <span className="text-ks-grey-1"> ({L(x.reason)})</span> : null}
            </>
          }
          quote={x.reason && x.src === "staff" ? (
            <>“{x.reason}”{x.team_alert_id && tkt(x.team_alert_id) ? <> · <IcLink id={tkt(x.team_alert_id)} /></> : null}</>
          ) : undefined}
          full={<Snapshot x={x} server={false} />}
        />
      ))}
    </ul>
  );
}
